import {v4 as uuid} from 'uuid';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {
  CallToAction,
  type GeneratedScript,
  type ScriptConfiguration,
} from '@contentlab/api/avatars/types';
import {ProjectType} from '@contentlab/api/project/types';
import type {
  ValidationErrors,
  CaptionsStylePreset,
} from '@contentlab/api/project/types';
import {AvatarKind, ConfigMode} from '@contentlab/types/aiAvatars';
import {AspectRatio} from '@contentlab/types/aspectRatio';
import {ElementPosition} from '@contentlab/types/elementPosition';
import {Resolution} from '@contentlab/types/resolution';
import {SrtItem} from '@contentlab/utils/srtUtils';
import {RgbaColor} from '@/types/rgbaColor';
import {CreditAccessAction} from '@/views/content-studio/utils/resolveCreditAccessAction';
import {AssetsQuerySorting} from '@/hooks/content-studio/use-assets-query/useAssetsQuery';
import {FeatureName} from '../types/project';
import {CUSTOM_GOAL_VALUE} from '../views/contentlab/pages/ai-avatar-page/constants/goalOptions';

export type UploadingFile = {
  file: File;
  id: string | null;
  status: FileStatus;
  progress: number;
  error: string | undefined;
  feature: FeatureName | string;
  metadata: {
    duration_millis: number;
    width: number;
    height: number;
    aspect_ratio: AspectRatio | null;
  };
};

export enum FileStatus {
  IN_PROGRESS = 'in_progress',
  SUCCESS = 'success',
  ERROR = 'error',
}

export type UploadedVideo = {
  id: string;
  isUploading: boolean;
  thumbnail?: string;
  fileName?: string;
  type: 'Uploads' | 'Results';
  url?: string;
  durationMillis: number;
  aspectRatio?: AspectRatio;
  width?: number;
  height?: number;
  feature: FeatureName | string;
};

export interface AssetErrors {
  assetId: string;
  errorCodes: string[];
  feature: FeatureName | string;
}

export interface PreviewAsset {
  assetId: string;
  fileName: string;
  file?: File;
}

export enum FilterOptions {
  FORMAT = 'format',
  LANGUAGE = 'language',
  SOURCE = 'source',
  PROJECT = 'project',
}

export type FilterOptionsState = {
  format: string[];
  language: string[];
  source: string[];
  project: string[];
};

export type SelectedAvatarAndVoicesFilters = {
  [filterId: string]: string[];
};

export type Asset = {
  id: string;
  thumbnailUrl: string;
};

export enum ErrorKind {
  VALIDATION = 'validation',
  FAILURE = 'failure',
}

export enum AvatarSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum AvatarConfigTarget {
  GLOBAL = 'config',
  LIBRARY = 'configLibrary',
  CUSTOM = 'configCustom',
}

export enum AiAvatarEditorTab {
  SCRIPTS = 'scripts',
  AVATARS = 'avatars',
}

export interface AvatarSpecConfig {
  removeBackground: boolean;
  circleMask: boolean;
  cropAvatar: boolean;
  avatarColor: RgbaColor | null;
  avatarSize: AvatarSize;
  avatarPosition: ElementPosition;
}

export const DEFAULT_AVATAR_SPECS_CONFIG = (): AvatarSpecConfig => ({
  removeBackground: true,
  circleMask: false,
  cropAvatar: false,
  avatarColor: null,
  avatarSize: AvatarSize.MEDIUM,
  avatarPosition: ElementPosition.BOTTOM_RIGHT,
});

export const DEFAULT_AVATAR_AUDIO_REDUCTION_FACTOR = 1.0;
export const DEFAULT_BACKGROUND_AUDIO_REDUCTION_FACTOR = 0.1;

type Error = {
  title: string;
  description: string;
  id: string;
  kind: ErrorKind;
};

type Alert = {
  id: string;
  title: string;
  description: string;
};

export interface BaseProject {
  isInitialized: boolean;
  projectName: string;
  // Server response fields
  projectId?: string;
  type?: ProjectType;
  validationErrors?: ValidationErrors | null;
}
export interface DraftRephrasingProject extends BaseProject {
  transcriptionLoaded: boolean;
  transcriptionUrl: string;
  currentSrt: SrtItem[];
  assetId: string;
  enableLipSync: boolean;
}

export interface DraftDubbingProject extends BaseProject {
  enableLipSync: boolean;
  selectedLanguages: string[];
}

export interface DraftReformatProject extends BaseProject {
  selectedAspectRatios: AspectRatio[];
}

export interface DraftUpscaleProject extends BaseProject {
  selectedResolutions: Resolution[];
}

export interface DraftCaptionsProject extends BaseProject {
  transcriptionLoaded: boolean;
  assetId: string;
  selectedStyle: CaptionsStylePreset | null;
  selectedLanguages: string[];
  originalLanguage: string; // TODO: drop it when API is updated
  apiOriginalLanguage?: {name: string; code: string} | null; // From API response
  outputTranscriptionAssetId?: string | null;
  currentSrt: SrtItem[];
  originalSrt: SrtItem[];
}

export interface PreGenerateForm {
  promoting: string;
  selectedGoal: CallToAction | typeof CUSTOM_GOAL_VALUE | null;
  customGoal: string;
}

export interface DraftAiAvatarProject extends BaseProject {
  assetId: string;
  scriptData: {
    scripts: GeneratedScript[];
    selectedScriptIndex: number | null;
  };
  scriptsSnapshot: GeneratedScript[];
  preGenerateForm: PreGenerateForm;
  avatarSpec: {
    voiceId: string | null; // legacy
    // Separate selections per mode
    voiceIdVoiceOnly?: string | null;
    voiceIdCustom?: string | null;
    libraryId: string | null;
    imageAssetId: string | null;
    imagePreviewUrl?: string | null;
    // Audio reduction factors
    avatarAudioReductionFactor: number;
    backgroundAudioReductionFactor: number;
    // Global fallback config (voice_only or legacy)
    config: AvatarSpecConfig;
    // Per-mode configs
    configLibrary?: AvatarSpecConfig;
    configCustom?: AvatarSpecConfig;
  };
  selectedAvatarKind: AvatarKind | null;
  activeEditorTab: AiAvatarEditorTab;
  configMode: ConfigMode;
  isAvatarLayoutOpen: boolean;
  isCustomizeScriptOpen: boolean;
  isAvatarPreviewMuted: boolean;
  lastSolidColor: RgbaColor | null;
}

export interface OnboardingState {
  targetFeature: FeatureName | null;
  isOnboardingModalOpen: boolean;
}

export type Option = {
  id: string;
  labelLocaleKey: string;
  icon?: React.ReactNode;
  optionalSubtitle?: string;
};

// Removed CustomizeScriptConfig - now using ScriptConfiguration directly

// Base project factory function
const baseProject = (): BaseProject => ({
  isInitialized: false,
  projectName: '',
  projectId: undefined,
  type: undefined,
  validationErrors: null,
});

type ContentLabState = {
  menuOpen: boolean;
  previewAssets: PreviewAsset[];
  activePreviewAssetId: string | null;
  uploadingFiles: Record<string, UploadingFile>;
  isUploading: boolean;
  uploadedVideos: UploadedVideo[];
  assetErrors: AssetErrors[];
  uploadsSorting: AssetsQuerySorting;
  resultsSorting: AssetsQuerySorting;
  uploadsFilters: FilterOptionsState;
  resultsFilters: FilterOptionsState;
  errors: Error[];
  isProjectShelfOpen: boolean;
  draftDubbingProject: DraftDubbingProject;
  draftReformatProject: DraftReformatProject;
  draftRephrasingProject: DraftRephrasingProject;
  draftCaptionsProject: DraftCaptionsProject;
  draftAiAvatarProject: DraftAiAvatarProject;
  draftUpscaleProject: DraftUpscaleProject;
  onboardingState: OnboardingState;
  isLearnMoreModalOpen: boolean;
  alerts: Alert[];
  isMuted: boolean;
  creditEndModalState: CreditAccessAction | null;
  originalCaptionStyles: CaptionsStylePreset[];
  editedStyles: Record<string, CaptionsStylePreset>;
  assetUploadProcessIdsMap: Record<string, string>;
  selectedAvatarFilters: SelectedAvatarAndVoicesFilters;
  selectedVoiceOnlyFilters: SelectedAvatarAndVoicesFilters;
};

const defaultDubbingProject = (): DraftDubbingProject => ({
  ...baseProject(),
  selectedLanguages: [],
  enableLipSync: true,
  type: ProjectType.DUBBING,
  validationErrors: null,
});

const defaultReformatProject = (): DraftReformatProject => ({
  ...baseProject(),
  selectedAspectRatios: [],
  type: ProjectType.REFORMAT,
  validationErrors: null,
});

const defaultUpscaleProject = (): DraftUpscaleProject => ({
  ...baseProject(),
  selectedResolutions: [],
  type: ProjectType.UPSCALE,
  validationErrors: null,
});

const defaultRephrasingProject = (): DraftRephrasingProject => ({
  ...baseProject(),
  transcriptionLoaded: false,
  transcriptionUrl: '',
  currentSrt: [],
  assetId: '',
  enableLipSync: true,
  type: ProjectType.REPHRASING,
  validationErrors: null,
});

const defaultCaptionsProject = (): DraftCaptionsProject => ({
  ...baseProject(),
  transcriptionLoaded: false,
  assetId: '',
  selectedStyle: null,
  selectedLanguages: [],
  originalLanguage: '', // Will be set from API response
  apiOriginalLanguage: null,
  outputTranscriptionAssetId: null,
  currentSrt: [],
  originalSrt: [],
  type: ProjectType.CAPTIONS,
  validationErrors: null,
});

const defaultAiAvatarProject = (): DraftAiAvatarProject => ({
  ...baseProject(),
  assetId: '',
  scriptData: {
    scripts: [],
    selectedScriptIndex: null,
  },
  scriptsSnapshot: [],
  preGenerateForm: {
    promoting: '',
    selectedGoal: null,
    customGoal: '',
  },
  avatarSpec: {
    voiceId: null,
    voiceIdVoiceOnly: null,
    voiceIdCustom: null,
    libraryId: null,
    imageAssetId: null,
    imagePreviewUrl: null,
    avatarAudioReductionFactor: DEFAULT_AVATAR_AUDIO_REDUCTION_FACTOR,
    backgroundAudioReductionFactor: DEFAULT_BACKGROUND_AUDIO_REDUCTION_FACTOR,
    config: DEFAULT_AVATAR_SPECS_CONFIG(),
    configLibrary: DEFAULT_AVATAR_SPECS_CONFIG(),
    configCustom: DEFAULT_AVATAR_SPECS_CONFIG(),
  },
  selectedAvatarKind: null,
  activeEditorTab: AiAvatarEditorTab.SCRIPTS,
  configMode: ConfigMode.LIBRARY,
  isAvatarLayoutOpen: false,
  isCustomizeScriptOpen: false,
  isAvatarPreviewMuted: false,
  lastSolidColor: null,
  type: ProjectType.AI_AVATAR,
  validationErrors: null,
});

const defaultOnboardingState = (): OnboardingState => ({
  targetFeature: null,
  isOnboardingModalOpen: false,
});

const initialState: ContentLabState = {
  menuOpen: true,
  previewAssets: [],
  activePreviewAssetId: null,
  uploadingFiles: {},
  isUploading: false,
  uploadedVideos: [],
  assetErrors: [],
  uploadsSorting: {field: 'created_at', desc: true},
  resultsSorting: {field: 'created_at', desc: true},
  uploadsFilters: {format: [], language: [], source: [], project: []},
  resultsFilters: {format: [], language: [], source: [], project: []},
  errors: [],
  isProjectShelfOpen: false,
  draftDubbingProject: defaultDubbingProject(),
  draftReformatProject: defaultReformatProject(),
  draftRephrasingProject: defaultRephrasingProject(),
  draftCaptionsProject: defaultCaptionsProject(),
  draftAiAvatarProject: defaultAiAvatarProject(),
  draftUpscaleProject: defaultUpscaleProject(),
  onboardingState: defaultOnboardingState(),
  isLearnMoreModalOpen: false,
  alerts: [],
  isMuted: false,
  creditEndModalState: null,
  originalCaptionStyles: [],
  editedStyles: {},
  assetUploadProcessIdsMap: {},
  selectedAvatarFilters: {},
  selectedVoiceOnlyFilters: {},
};

const contentLabStore = create<ContentLabState>()(
  devtools(immer(() => initialState))
);

export const contentLabActions = {
  setMenuOpen: (isOpen: boolean) =>
    contentLabStore.setState((state) => {
      state.menuOpen = isOpen;
    }),
  setPreviewAssets: (previewAssets: PreviewAsset[]) =>
    contentLabStore.setState((state) => {
      state.previewAssets = previewAssets;
    }),
  setActivePreviewAssetId: (id: string | null) =>
    contentLabStore.setState((state) => {
      state.activePreviewAssetId = id;
    }),
  setUploadedVideos: (videos: UploadedVideo[]) =>
    contentLabStore.setState((state) => {
      state.uploadedVideos = videos;
    }),
  setUploadsSorting: (sorting: AssetsQuerySorting) =>
    contentLabStore.setState((state) => {
      state.uploadsSorting = sorting;
    }),
  setResultsSorting: (sorting: AssetsQuerySorting) =>
    contentLabStore.setState((state) => {
      state.resultsSorting = sorting;
    }),
  addUploadingFiles: (newFiles: UploadingFile[]) =>
    contentLabStore.setState((state) => {
      newFiles.forEach((file) => {
        state.uploadingFiles[file.file.name] = file;
      });
    }),
  addUploadedVideos: (newVideos: UploadedVideo[]) =>
    contentLabStore.setState((state) => {
      state.uploadedVideos.push(...newVideos);
    }),
  addAssetErrors: (newAssetErrors: AssetErrors[]) =>
    contentLabStore.setState((state) => {
      state.assetErrors.push(...newAssetErrors);
    }),
  updateUploadedVideo: (video: Partial<UploadedVideo> & {id: string}) =>
    contentLabStore.setState((state) => {
      const index = state.uploadedVideos.findIndex((v) => v.id === video.id);
      if (index !== -1) {
        state.uploadedVideos[index] = {
          ...state.uploadedVideos[index],
          ...video,
        };
      }
    }),
  updateUploadingFile: ({
    name,
    ...updates
  }: {name: string} & Partial<UploadingFile>) =>
    contentLabStore.setState((state) => {
      if (state.uploadingFiles[name]) {
        state.uploadingFiles[name] = {
          ...state.uploadingFiles[name],
          ...updates,
        };
      }
    }),
  removeUploadingFile: (fileName: string) =>
    contentLabStore.setState((state) => {
      delete state.uploadingFiles[fileName];
    }),
  removeUploadingFiles: (fileNames: string[]) =>
    contentLabStore.setState((state) => {
      fileNames.forEach((fileName) => {
        delete state.uploadingFiles[fileName];
      });
    }),
  removeUploadedVideo: (videoId: string) =>
    contentLabStore.setState((state) => {
      state.uploadedVideos = state.uploadedVideos.filter(
        (v) => v.id !== videoId
      );
    }),
  removeAssetErrors: (assetId: string) =>
    contentLabStore.setState((state) => {
      state.assetErrors = state.assetErrors.filter(
        (assetError) => assetError.assetId !== assetId
      );
    }),
  removeFeatureUploadingFiles: (feature: FeatureName | string) =>
    contentLabStore.setState((state) => {
      state.uploadingFiles = Object.fromEntries(
        Object.entries(state.uploadingFiles).filter(
          ([, file]) => file.feature !== feature
        )
      );
    }),
  removeFeatureUploadedVideos: (feature: FeatureName | string) =>
    contentLabStore.setState((state) => {
      state.uploadedVideos = state.uploadedVideos.filter(
        (v) => v.feature !== feature
      );
    }),
  removeFeatureAssetErrors: (feature: FeatureName | string) =>
    contentLabStore.setState((state) => {
      state.assetErrors = state.assetErrors.filter(
        (assetError) => assetError.feature !== feature
      );
    }),
  getUploadingVideoByAssetId: (assetId: string): UploadingFile | undefined => {
    const state = contentLabStore.getState();
    return Object.values(state.uploadingFiles).find((f) => f.id === assetId);
  },
  getUploadedVideoByAssetId: (assetId: string): UploadedVideo | undefined => {
    const state = contentLabStore.getState();
    return state.uploadedVideos.find((f) => f.id === assetId);
  },
  handleFilter: (
    filter: keyof FilterOptionsState,
    value: string,
    type: 'uploads' | 'results'
  ) =>
    contentLabStore.setState((state) => {
      const targetFilters =
        type === 'uploads' ? state.uploadsFilters : state.resultsFilters;
      const index = targetFilters[filter].indexOf(value);
      if (index === -1) {
        targetFilters[filter].push(value);
      } else {
        targetFilters[filter].splice(index, 1);
      }
    }),
  setProjectFilters: (project: string | undefined) =>
    contentLabStore.setState((state) => {
      state.resultsFilters.project = project ? [project] : [];
    }),
  addError: ({
    title,
    description,
    kind,
  }: {
    title: string;
    description: string;
    kind: ErrorKind;
  }) =>
    contentLabStore.setState((state) => {
      state.errors.push({title, description, id: uuid(), kind});
    }),
  removeError: (id: string) =>
    contentLabStore.setState((state) => {
      state.errors = state.errors.filter((error) => error.id !== id);
    }),
  clearErrors: () =>
    contentLabStore.setState((state) => {
      state.errors = [];
    }),
  addAlert: ({title, description}: {title: string; description: string}) =>
    contentLabStore.setState((state) => {
      state.alerts.push({id: uuid(), title, description});
    }),
  removeAlert: (id: string) =>
    contentLabStore.setState((state) => {
      state.alerts = state.alerts.filter((alert) => alert.id !== id);
    }),
  clearAlerts: () =>
    contentLabStore.setState((state) => {
      state.alerts = [];
    }),
  updateDraftRephrasingProjectTranscriptionLoaded: (
    transcriptionLoaded: boolean
  ) =>
    contentLabStore.setState((state) => {
      state.draftRephrasingProject.transcriptionLoaded = transcriptionLoaded;
    }),
  updateDraftRephrasingProjectSrt: (srt: SrtItem[]) =>
    contentLabStore.setState((state) => {
      state.draftRephrasingProject.currentSrt = srt;
    }),
  updateDraftRephrasingProjectName: (name: string) =>
    contentLabStore.setState((state) => {
      state.draftRephrasingProject.projectName = name;
    }),
  updateDraftRephrasingProjectLipSync: (lipSync: boolean) =>
    contentLabStore.setState((state) => {
      state.draftRephrasingProject.enableLipSync = lipSync;
    }),
  updateDraftRephrasingProjectAssetId: (assetId: string) =>
    contentLabStore.setState((state) => {
      state.draftRephrasingProject.assetId = assetId;
    }),
  resetDraftRephrasingProject: () =>
    contentLabStore.setState((state) => {
      state.draftRephrasingProject = defaultRephrasingProject();
    }),
  updateDraftCaptionsProjectTranscriptionLoaded: (
    transcriptionLoaded: boolean
  ) =>
    contentLabStore.setState((state) => {
      state.draftCaptionsProject.transcriptionLoaded = transcriptionLoaded;
    }),

  updateDraftCaptionsProjectName: (name: string) =>
    contentLabStore.setState((state) => {
      state.draftCaptionsProject.projectName = name;
    }),
  updateDraftCaptionsProjectAssetId: (assetId: string) =>
    contentLabStore.setState((state) => {
      state.draftCaptionsProject.assetId = assetId;
    }),

  updateDraftCaptionsProjectLanguages: (languages: string[]) =>
    contentLabStore.setState((state) => {
      state.draftCaptionsProject.selectedLanguages = languages;
    }),
  updateDraftCaptionsProjectSrt: (srt: SrtItem[]) =>
    contentLabStore.setState((state) => {
      state.draftCaptionsProject.currentSrt = srt;
    }),
  resetDraftCaptionsProject: () =>
    contentLabStore.setState((state) => {
      state.draftCaptionsProject = defaultCaptionsProject();
    }),
  updateDraftDubbingProjectLanguages: (languages: string[]) =>
    contentLabStore.setState((state) => {
      state.draftDubbingProject.selectedLanguages = languages;
    }),
  updateDraftDubbingProjectName: (name: string) =>
    contentLabStore.setState((state) => {
      state.draftDubbingProject.projectName = name;
    }),
  updateDraftDubbingProjectLipSync: (lipSync: boolean) =>
    contentLabStore.setState((state) => {
      state.draftDubbingProject.enableLipSync = lipSync;
    }),
  resetDraftDubbingProject: () =>
    contentLabStore.setState((state) => {
      state.draftDubbingProject = defaultDubbingProject();
    }),
  updateDraftReformatProjectAspectRatios: (aspectRatios: AspectRatio[]) =>
    contentLabStore.setState((state) => {
      state.draftReformatProject.selectedAspectRatios = aspectRatios;
    }),
  updateDraftReformatProjectName: (name: string) =>
    contentLabStore.setState((state) => {
      state.draftReformatProject.projectName = name;
    }),
  resetDraftReformatProject: () =>
    contentLabStore.setState((state) => {
      state.draftReformatProject = defaultReformatProject();
    }),
  updateDraftUpscaleProjectResolutions: (resolutions: Resolution[]) =>
    contentLabStore.setState((state) => {
      state.draftUpscaleProject.selectedResolutions = resolutions;
    }),
  updateDraftUpscaleProjectName: (name: string) =>
    contentLabStore.setState((state) => {
      state.draftUpscaleProject.projectName = name;
    }),
  resetDraftUpscaleProject: () =>
    contentLabStore.setState((state) => {
      state.draftUpscaleProject = defaultUpscaleProject();
    }),
  setIsProjectShelfOpen: (isOpen: boolean) =>
    contentLabStore.setState((state) => {
      state.isProjectShelfOpen = isOpen;
    }),
  setDraftDubbingProject: (project: DraftDubbingProject) =>
    contentLabStore.setState((state) => {
      state.draftDubbingProject = project;
    }),
  setDraftReformatProject: (project: DraftReformatProject) =>
    contentLabStore.setState((state) => {
      state.draftReformatProject = project;
    }),
  setDraftUpscaleProject: (project: DraftUpscaleProject) =>
    contentLabStore.setState((state) => {
      state.draftUpscaleProject = project;
    }),
  setDraftRephrasingProject: (project: DraftRephrasingProject) =>
    contentLabStore.setState((state) => {
      state.draftRephrasingProject = project;
    }),
  setDraftCaptionsProject: (project: DraftCaptionsProject) =>
    contentLabStore.setState((state) => {
      state.draftCaptionsProject = project;
    }),
  // AI Avatar draft actions
  setDraftAiAvatarProjectAvatarSpec: (
    avatarSpec: DraftAiAvatarProject['avatarSpec']
  ) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject.avatarSpec = avatarSpec;
    }),
  setDraftAiAvatarProjectSelectedType: (
    avatarKind: DraftAiAvatarProject['selectedAvatarKind']
  ) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject.selectedAvatarKind = avatarKind ?? null;
    }),
  updateDraftAiAvatarProjectAvatarSpec: (
    avatarSpec: Partial<DraftAiAvatarProject['avatarSpec']>
  ) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject.avatarSpec = {
        ...state.draftAiAvatarProject.avatarSpec,
        ...avatarSpec,
      };
    }),
  updateDraftAiAvatarProjectScriptData: (
    scriptData: Partial<DraftAiAvatarProject['scriptData']>
  ) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject.scriptData = {
        ...state.draftAiAvatarProject.scriptData,
        ...scriptData,
      };
    }),
  updateDraftAiAvatarProject: (
    updates: Omit<
      Partial<DraftAiAvatarProject>,
      'avatarSpec' | 'scriptData' | 'config' | 'configLibrary' | 'configCustom'
    >
  ) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject = {
        ...state.draftAiAvatarProject,
        ...updates,
      };
    }),
  updateDraftAiAvatarProjectConfig: (
    config: Partial<DraftAiAvatarProject['avatarSpec']['config']>,
    mode?: ConfigMode
  ) =>
    contentLabStore.setState((state) => {
      const effectiveMode = mode ?? state.draftAiAvatarProject.configMode;
      const spec = state.draftAiAvatarProject.avatarSpec;
      if (effectiveMode === ConfigMode.LIBRARY) {
        const base = spec.configLibrary ?? spec.config;
        spec.configLibrary = {...base, ...config};
        return;
      }
      if (effectiveMode === ConfigMode.CUSTOM) {
        const base = spec.configCustom ?? spec.config;
        spec.configCustom = {...base, ...config};
      }
    }),
  setDraftConfigMode: (mode: ConfigMode) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject.configMode = mode;
    }),
  setDraftIsAvatarLayoutOpen: (isOpen: boolean) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject.isAvatarLayoutOpen = isOpen;
    }),
  setDraftIsCustomizeScriptOpen: (isOpen: boolean) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject.isCustomizeScriptOpen = isOpen;
    }),
  updatePreGenerateForm: (updates: Partial<PreGenerateForm>) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject.preGenerateForm = {
        ...state.draftAiAvatarProject.preGenerateForm,
        ...updates,
      };
    }),
  setDraftAiAvatarProjectInitialized: (isInitialized: boolean) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject.isInitialized = isInitialized;
    }),
  resetDraftAiAvatarProject: () =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject = defaultAiAvatarProject();
    }),
  setDraftAiAvatarProject: (project: DraftAiAvatarProject) =>
    contentLabStore.setState((state) => {
      state.draftAiAvatarProject = project;
    }),
  // Avatar filters actions
  setSelectedAvatarFilters: (filters: SelectedAvatarAndVoicesFilters) =>
    contentLabStore.setState((state) => {
      state.selectedAvatarFilters = filters;
    }),
  updateSelectedAvatarFilters: (filterId: string, selectedOptionId: string[]) =>
    contentLabStore.setState((state) => {
      state.selectedAvatarFilters[filterId] = selectedOptionId;
    }),
  resetSelectedAvatarFilters: () =>
    contentLabStore.setState((state) => {
      state.selectedAvatarFilters = {};
    }),
  // Voice-only filters actions
  setSelectedVoiceOnlyFilters: (filters: SelectedAvatarAndVoicesFilters) =>
    contentLabStore.setState((state) => {
      state.selectedVoiceOnlyFilters = filters;
    }),
  updateSelectedVoiceOnlyFilters: (
    filterId: string,
    selectedOptionId: string[]
  ) =>
    contentLabStore.setState((state) => {
      state.selectedVoiceOnlyFilters[filterId] = selectedOptionId;
    }),
  resetSelectedVoiceOnlyFilters: () =>
    contentLabStore.setState((state) => {
      state.selectedVoiceOnlyFilters = {};
    }),
  updateScriptConfiguration: (
    scriptIndex: number,
    config: Partial<ScriptConfiguration>
  ) =>
    contentLabStore.setState((state) => {
      const script = state.draftAiAvatarProject.scriptData.scripts[scriptIndex];
      if (script) {
        script.inputScriptConfiguration = {
          ...script.inputScriptConfiguration,
          ...config,
        };
      }
    }),
  getScriptConfiguration: (
    scriptIndex: number
  ): ScriptConfiguration | undefined => {
    const state = contentLabStore.getState();
    return state.draftAiAvatarProject.scriptData.scripts[scriptIndex]
      ?.inputScriptConfiguration;
  },
  setOnboardingTargetFeature: (feature: FeatureName | null) =>
    contentLabStore.setState((state) => {
      state.onboardingState.targetFeature = feature;
    }),
  setIsOnboardingModalOpen: (isOpen: boolean) =>
    contentLabStore.setState((state) => {
      state.onboardingState.isOnboardingModalOpen = isOpen;
    }),
  setIsLearnMoreModalOpen: (isOpen: boolean) =>
    contentLabStore.setState((state) => {
      state.isLearnMoreModalOpen = isOpen;
    }),
  setIsMuted: (isMuted: boolean) =>
    contentLabStore.setState((state) => {
      state.isMuted = isMuted;
    }),
  setCreditEndModalState: (creditEndModalState: CreditAccessAction | null) =>
    contentLabStore.setState((state) => {
      state.creditEndModalState = creditEndModalState;
    }),
  setOriginalCaptionStyles: (styles: CaptionsStylePreset[]) =>
    contentLabStore.setState((state) => {
      state.originalCaptionStyles = styles;
    }),
  resetStyleToOriginal: (styleId: string) =>
    contentLabStore.setState((state) => {
      delete state.editedStyles[styleId];
    }),
  clearEditedStyles: () =>
    contentLabStore.setState((state) => {
      state.editedStyles = {};
    }),
  saveEditedStyle: (style: CaptionsStylePreset) =>
    contentLabStore.setState((state) => {
      state.editedStyles[style.id] = style;
    }),
  getEditedStyle: (styleId: string): CaptionsStylePreset | null => {
    const state = contentLabStore.getState();
    return state.editedStyles[styleId] || null;
  },
  getEditedStyleIds: (): Set<string> => {
    const state = contentLabStore.getState();
    return new Set(Object.keys(state.editedStyles));
  },
  addAssetUploadProcessId: ({
    assetId,
    uploadProcessId,
  }: {
    assetId: string;
    uploadProcessId: string;
  }) => {
    contentLabStore.setState((state) => {
      state.assetUploadProcessIdsMap = {
        ...state.assetUploadProcessIdsMap,
        [assetId]: uploadProcessId,
      };
    });
  },
  removeAssetUploadProcessId: ({assetId}: {assetId: string}) => {
    contentLabStore.setState((state) => {
      delete state.assetUploadProcessIdsMap[assetId];
    });
  },
};

export default contentLabStore;
